import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { servicesData } from "./ServicesData";
import { Send, CheckCircle2, X } from "lucide-react";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

const ServicesDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.id === serviceId);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service)
    return (
      <div className="py-40 text-center text-white bg-black h-screen">
        Service Not Found
      </div>
    );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen font-sans">
      <Header />

      {/* --- HERO SECTION --- */}
      <div
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-fixed bg-center"
        style={{ backgroundImage: `url(${service.bannerImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-[#0a0a0a]"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <span className="text-[#b08d57] uppercase tracking-[0.3em] text-sm font-bold mb-4 block">
            Legal Practice Area
          </span>
          <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 italic">
            {service.title}
          </h1>
          <div className="w-24 h-1 bg-[#b08d57] mx-auto"></div>
        </div>
      </div>

      {/* --- MAIN CONTENT AREA --- */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* LEFT: CONTENT (70%) */}
          <div className="lg:w-2/3 space-y-16">
            {/* OVERVIEW */}
            <section>
              <h2 className="text-3xl font-serif font-semibold mb-8 border-l-4 border-[#b08d57] pl-6">
                Service Overview
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-[#b08d57] first-letter:mr-3 first-letter:float-left">
                {service.fullDesc?.intro}
              </p>
            </section>

            {/* DYNAMIC SECTIONS */}
            {service.fullDesc?.sections?.map((sec, i) => (
              <section
                key={i}
                className="bg-white/5 p-8 rounded-2xl border border-white/10"
              >
                <h3 className="text-2xl font-semibold text-[#b08d57] mb-4">
                  {sec.title}
                </h3>
                {sec.content && (
                  <p className="text-gray-400 mb-6">{sec.content}</p>
                )}

                {sec.list && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {sec.list.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 bg-black/30 p-4 rounded-lg border border-white/5 hover:border-[#b08d57]/50 transition"
                      >
                        <CheckCircle2
                          className="text-[#b08d57] mt-1 shrink-0"
                          size={18}
                        />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* CASE HIGHLIGHTS */}
            <section>
              <h3 className="text-2xl font-serif font-semibold mb-8">
                Recent Case Highlights
              </h3>
              <div className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide">
                {service.featureDetails?.map((item, i) => (
                  <div
                    key={i}
                    onClick={() => setSelectedItem(item)}
                    className="group min-w-[300px] bg-[#161616] rounded-xl overflow-hidden cursor-pointer border border-white/10 hover:border-[#b08d57] transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.name}
                        className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent"></div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-lg mb-2 group-hover:text-[#b08d57] transition-colors">
                        {item.name}
                      </h4>
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* RIGHT: STICKY FORM (30%) */}
          <div className="lg:w-1/3">
            <div className="sticky top-28 bg-[#111] border border-[#b08d57]/30 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-2xl font-serif font-bold mb-2 text-[#b08d57]">
                Free Consultation
              </h3>
              <p className="text-gray-400 text-sm mb-8">
                Schedule a confidential meeting with our experts.
              </p>

              <form className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-[#b08d57] outline-none transition"
                    placeholder="Full Name"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-[#b08d57] outline-none transition"
                    placeholder="Email Address"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-[#b08d57] outline-none transition"
                    placeholder="Phone Number"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-[#b08d57] outline-none transition"
                    placeholder="Your Message"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const { name, email, phone, message } = formData;

                    if (!name || !phone) {
                      alert("Name and Phone are required!");
                      return;
                    }

                    const text = `New Inquiry:%0A
                   Name: ${name}%0A
                    Email: ${email}%0A
                 Phone: ${phone}%0A
               Message: ${message}`;

                    const whatsappUrl = `https://wa.me/918650337072?text=${text}`; // 👉 apna number daalo

                    window.open(whatsappUrl, "_blank");
                  }}
                  className="w-full bg-[#b08d57] hover:bg-[#967648] text-black font-bold py-4 rounded-lg flex items-center justify-center gap-3 mt-6 transition-all transform active:scale-95 shadow-lg shadow-[#b08d57]/20"
                >
                  Request Callback <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* POPUP MODAL */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-[#111] border border-white/10 rounded-2xl max-w-2xl w-full overflow-hidden relative">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 text-white/50 hover:text-white z-10"
            >
              <X size={24} />
            </button>
            <div className="flex flex-col md:flex-row">
              <img
                src={selectedItem.img}
                className="w-full md:w-1/2 h-64 md:h-auto object-cover"
                alt="Case detail"
              />
              <div className="p-8 md:w-1/2">
                <h2 className="text-2xl font-bold mb-4 text-[#b08d57]">
                  {selectedItem.name}
                </h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {selectedItem.desc}
                </p>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-[#b08d57] font-semibold border-b border-[#b08d57] pb-1"
                >
                  Back to Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ServicesDetail;
